import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

const DEFAULT_TRAINING_PROMPT = `You are a helpful, professional booking assistant for BookaTherapy - a platform that connects clients with professional massage therapists and wellness practitioners.

YOUR ROLE:
- Answer questions about BookaTherapy and our services
- Guide clients through the booking process
- Provide helpful information about therapy types, pricing, and booking options
- Be friendly, patient, and informative

ABOUT BOOKATHERAPY:
- We connect clients with licensed massage therapists and wellness practitioners
- Services available: Massage therapy (Swedish, deep tissue, sports, prenatal, etc.)
- Booking options: In-clinic appointments, mobile (therapist comes to you), or home-based services
- Payment: Secure online payment via Stripe or pay at clinic
- Coverage: Multiple cities across Canada (ask for their city to check availability)
- All therapists are licensed, background-checked professionals

CONVERSATION APPROACH:
- Keep responses concise but informative (1-3 sentences)
- Answer questions directly - don't redirect to support unless truly necessary
- When helping with booking, ask ONE question at a time
- Be conversational and warm, not robotic

BOOKING FLOW (when user wants to book):
1. Ask what brings them in (e.g., stress relief, pain management, relaxation)
2. Ask for their city/location
3. Ask about service preference (clinic, mobile, or home-based)
4. Ask about therapist gender preference (if any)
5. Ask about preferred date and time
6. Collect contact info (name, email, phone)
7. Guide them to payment/confirmation

IMPORTANT:
- ALWAYS try to answer questions - you have knowledge about BookaTherapy services
- Only say you can't help if the question is truly outside your scope (medical advice, technical issues, etc.)
- Be helpful and informative - your goal is to make booking easy and answer questions
- Never leave users hanging - always provide a helpful response or next step`;

export default function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hello! How can I help you today?' }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [trainingPrompt, setTrainingPrompt] = useState(DEFAULT_TRAINING_PROMPT);
  const messagesEndRef = useRef(null);
  const { toast } = useToast();

  // Load training prompt on mount
  useEffect(() => {
    loadTrainingPrompt();
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadTrainingPrompt = async () => {
    try {
      // Try to get from backend first
      const data = await api.chatbot.getTraining();
      if (data?.prompt) {
        setTrainingPrompt(data.prompt);
      }
    } catch (error) {
      console.error('Error loading training prompt:', error);
      // Fallback to localStorage
      const stored = localStorage.getItem('bookatherapy_chatbot_training');
      if (stored) {
        setTrainingPrompt(stored);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');

    // Add user message to chat
    const newMessages = [...messages, { role: 'user', content: userMessage }];
    setMessages(newMessages);
    setIsLoading(true);

    try {
      // Send to backend with training prompt
      const response = await api.chatbot.sendMessage({
        messages: newMessages,
        systemPrompt: trainingPrompt
      });

      // Add AI response
      if (response?.reply) {
        setMessages([...newMessages, { role: 'assistant', content: response.reply }]);
      } else {
        throw new Error('No reply from AI');
      }
    } catch (error) {
      console.error('Chatbot error:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to get response. Please try again.',
        variant: 'destructive'
      });

      // Add error message
      setMessages([
        ...newMessages,
        { role: 'assistant', content: 'Sorry, I had trouble responding. Please try again.' }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setMessages([{ role: 'assistant', content: 'Hello! How can I help you today?' }]);
    toast({
      title: 'Chat Reset',
      description: 'Conversation has been cleared.'
    });
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 w-14 h-14 sm:w-16 sm:h-16 bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] rounded-full shadow-2xl flex items-center justify-center transition-all duration-300 hover:scale-110 z-50 group"
          aria-label="Open chat"
        >
          <MessageCircle className="h-7 w-7" />
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse">
            1
          </span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[400px] sm:h-[600px] sm:max-h-[80vh] bg-white sm:rounded-2xl shadow-2xl flex flex-col z-50 sm:border-2 sm:border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-[#252F4F] to-[#1e2640] text-white p-4 sm:rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#98C454] rounded-full flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-[#252F4F]" />
              </div>
              <div>
                <h3 className="font-bold text-lg">BookaTherapy</h3>
                <p className="text-xs text-gray-300">Online • Ready to help</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/10 p-2 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-[#98C454] text-[#252F4F] rounded-br-sm'
                      : 'bg-white text-gray-800 border border-gray-200 rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 border border-gray-200 px-4 py-3 rounded-2xl rounded-bl-sm flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Typing...</span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t border-gray-200 bg-white sm:rounded-b-2xl safe-bottom">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder="Type your message..."
                disabled={isLoading}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#98C454] focus:border-transparent disabled:bg-gray-100 text-sm"
              />
              <button
                type="submit"
                disabled={!inputMessage.trim() || isLoading}
                className="bg-[#98C454] hover:bg-[#87b043] text-[#252F4F] px-5 py-3 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                <Send className="h-5 w-5" />
              </button>
            </form>

            {/* Reset Button */}
            <button
              onClick={handleReset}
              className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Reset conversation
            </button>
          </div>
        </div>
      )}
    </>
  );
}
